import type { EditOp, ResolvedEdit } from "@desk/core";
import { effectiveSound, isAudibleSound } from "@desk/core";

/** Music bed cards in simple Sound → Music (None/silence + 3 audible moods). */
export type MusicMoodId = "clean" | "warm" | "pulse" | "sector";

/** @deprecated Prefer MusicMoodId — kept for existing imports/tests. */
export type SoundTreatmentId = MusicMoodId | "suspense";

export const MUSIC_MOOD_DEFS: ReadonlyArray<{
  id: MusicMoodId;
  label: string;
  /** null = silence / None (no music bed) */
  assetId: string | null;
  /** Preserved testid suffix (warm = Airy bed; none = silence). */
  testId: string;
}> = [
  { id: "clean", label: "None", assetId: null, testId: "none" },
  { id: "warm", label: "Warm", assetId: "sfx-soft-bed", testId: "warm" },
  { id: "sector", label: "Tense", assetId: "music-sector", testId: "sector" },
  { id: "pulse", label: "Upbeat", assetId: "music-pulse", testId: "pulse" },
];

export function isStingLikeAsset(asset: {
  id: string;
  tags: readonly string[];
}): boolean {
  return (
    asset.tags.includes("ominous") ||
    asset.tags.includes("alarm") ||
    asset.tags.includes("sting")
  );
}

export function isMusicBedAsset(asset: {
  id: string;
  tags: readonly string[];
}): boolean {
  return (
    asset.tags.includes("bed") ||
    asset.tags.includes("music") ||
    asset.id === "sfx-soft-bed" ||
    asset.id.startsWith("music-")
  );
}

/**
 * Derive selected Music card from the effective resolved sound (last-wins).
 * Maps airy/soft bed → "warm" to preserve sound-treatment-warm contracts.
 */
export function musicMoodFromResolved(
  resolved: ResolvedEdit,
  opts?: {
    warmAssetId?: string | null;
    assetTags?: (assetId: string) => readonly string[];
  },
): MusicMoodId | null {
  const sound = effectiveSound(resolved);
  // No effective sound → Clean (hydration / undo-to-empty / fresh pack).
  if (!sound) return "clean";
  if (!isAudibleSound(sound)) return "clean";
  if (sound.assetId === "music-pulse") return "pulse";
  if (sound.assetId === "music-sector") return "sector";
  const warmId = opts?.warmAssetId ?? "sfx-soft-bed";
  const tags = opts?.assetTags?.(sound.assetId) ?? [];
  if (
    sound.assetId === warmId ||
    tags.includes("warm") ||
    tags.includes("soft") ||
    tags.includes("airy")
  ) {
    return "warm";
  }
  // Other audible beds (custom Fine tune) still count as warm-ish Airy lane for UI.
  if (sound.role === "bed" || sound.role === "ambience") return "warm";
  return null;
}

/** @deprecated Use musicMoodFromResolved — suspense is Fine-tune/store only. */
export function soundTreatmentFromResolved(
  resolved: ResolvedEdit,
  opts: {
    warmAssetId: string | null;
    suspenseAssetId: string | null;
    assetTags?: (assetId: string) => readonly string[];
  },
): SoundTreatmentId | null {
  const mood = musicMoodFromResolved(resolved, opts);
  if (mood) return mood;
  const sound = effectiveSound(resolved);
  if (!sound || !isAudibleSound(sound)) return mood;
  const tags = opts.assetTags?.(sound.assetId) ?? [];
  if (
    sound.role === "sting" ||
    sound.assetId === opts.suspenseAssetId ||
    tags.includes("ominous") ||
    tags.includes("alarm") ||
    tags.includes("sting")
  ) {
    return "suspense";
  }
  return null;
}

/**
 * Focus is a single ZOOM control plus drag-to-pan on the video (no presets, no
 * subject buttons). Zoom is expressed as a percentage: 100 = 1.0x (Wide, full
 * frame, no crop) up to 250 = 2.5x (tight). A zoom step shrinks the normalized
 * crop rect symmetrically around its centre; panning moves the crop x/y. The
 * renderer already consumes this crop rect, and the emotion model reads its
 * width/height, so a tighter zoom keeps pushing Pity/Doubt exactly as before.
 */
export const FOCUS_ZOOM_MIN_PCT = 100;
export const FOCUS_ZOOM_MAX_PCT = 250;
export const FOCUS_ZOOM_STEP_PCT = 10;

/** Normalized rect the renderer + emotion model consume (millipoints, 0..1000). */
export interface FocusCrop {
  xMilli: number;
  yMilli: number;
  wMilli: number;
  hMilli: number;
}

const FULL_CROP: FocusCrop = { xMilli: 0, yMilli: 0, wMilli: 1000, hMilli: 1000 };

/** Snap a zoom percentage to the slider range/step. */
export function clampFocusZoomPct(pct: number): number {
  const stepped = Math.round(pct / FOCUS_ZOOM_STEP_PCT) * FOCUS_ZOOM_STEP_PCT;
  return Math.max(FOCUS_ZOOM_MIN_PCT, Math.min(FOCUS_ZOOM_MAX_PCT, stepped));
}

/** Square crop side (millipoints) for a zoom percentage: 100 -> 1000, 250 -> 400. */
export function focusCropSizeForZoom(pct: number): number {
  const size = Math.round(100000 / clampFocusZoomPct(pct));
  return Math.max(400, Math.min(1000, size));
}

/**
 * Crop rect for a zoom percentage, keeping the previous crop's centre so zooming
 * feels like it happens in place. At 1.0x the rect is the full frame (no crop).
 */
export function focusCropForZoom(pct: number, prev?: FocusCrop | null): FocusCrop {
  const size = focusCropSizeForZoom(pct);
  if (size >= 1000) return { ...FULL_CROP };
  const cx = prev ? prev.xMilli + prev.wMilli / 2 : 500;
  const cy = prev ? prev.yMilli + prev.hMilli / 2 : 500;
  const xMilli = Math.max(0, Math.min(1000 - size, Math.round(cx - size / 2)));
  const yMilli = Math.max(0, Math.min(1000 - size, Math.round(cy - size / 2)));
  return { xMilli, yMilli, wMilli: size, hMilli: size };
}

/**
 * Square crop that FRAMES a normalized subject rect (0..1): centred on the rect
 * and sized to contain it (with a little breathing room), clamped inside the
 * frame and to the zoom range's min crop side. Returns millipoints (the same
 * normalized rect the renderer + emotion model consume). Framing on a subject
 * "hides the rest" (a tighter square around the subject), and the caller stamps
 * the subject's entity id onto the frame op so the crop->emotion lever reads it.
 */
export function focusCropForSubject(rect: {
  x: number;
  y: number;
  w: number;
  h: number;
}): FocusCrop {
  const pad = 1.15;
  const sideNorm = Math.max(rect.w, rect.h) * pad;
  const size = Math.max(400, Math.min(1000, Math.round(sideNorm * 1000)));
  if (size >= 1000) return { ...FULL_CROP };
  const cxMilli = Math.round((rect.x + rect.w / 2) * 1000);
  const cyMilli = Math.round((rect.y + rect.h / 2) * 1000);
  const xMilli = Math.max(0, Math.min(1000 - size, cxMilli - Math.round(size / 2)));
  const yMilli = Math.max(0, Math.min(1000 - size, cyMilli - Math.round(size / 2)));
  return { xMilli, yMilli, wMilli: size, hMilli: size };
}

/**
 * The slider zoom percentage a crop represents: a full (or missing) crop reads
 * as 1.0x; otherwise inverts focusCropSizeForZoom off the smaller side. Shared by
 * the slider (from a resolved frame) and the wheel/pinch handlers (from a live
 * crop) so all three agree on the current zoom.
 */
export function focusZoomPctFromCrop(crop: FocusCrop | null | undefined): number {
  if (!crop) return FOCUS_ZOOM_MIN_PCT;
  const size = Math.min(crop.wMilli || 1000, crop.hMilli || 1000);
  if (size >= 1000) return FOCUS_ZOOM_MIN_PCT;
  return clampFocusZoomPct(Math.round(100000 / Math.max(1, size)));
}

/** Reposition a crop to a new top-left, clamped inside the frame (pan). */
export function focusPanCrop(crop: FocusCrop, xMilli: number, yMilli: number): FocusCrop {
  const w = crop.wMilli;
  const h = crop.hMilli;
  return {
    wMilli: w,
    hMilli: h,
    xMilli: Math.max(0, Math.min(1000 - w, Math.round(xMilli))),
    yMilli: Math.max(0, Math.min(1000 - h, Math.round(yMilli))),
  };
}

/** On-screen box position for a subject marker, as CSS percentages (0..100). */
export interface SubjectScreenRect {
  leftPct: number;
  topPct: number;
  widthPct: number;
  heightPct: number;
}

/**
 * Project a normalized subject rect (0..1 in full-frame coords) into the position
 * it occupies ON SCREEN given the current crop, as CSS percentages.
 *
 * At Wide (full-frame crop) this is the identity (`rect * 100`), matching the
 * un-zoomed box placement. When zoomed, only the crop window is shown blown up to
 * fill the container, so subjects must SHIFT by the crop origin and SCALE by the
 * crop size — this keeps a subject marker locked onto the same footage as you zoom
 * and pan, instead of vanishing. A subject outside the crop window lands off the
 * 0..100 range and is clipped by the container's overflow.
 */
export function subjectRectToScreenPct(
  rect: { x: number; y: number; w: number; h: number },
  crop: FocusCrop | null | undefined,
): SubjectScreenRect {
  const cx = (crop?.xMilli ?? 0) / 1000;
  const cy = (crop?.yMilli ?? 0) / 1000;
  const cw = (crop?.wMilli || 1000) / 1000;
  const ch = (crop?.hMilli || 1000) / 1000;
  return {
    leftPct: ((rect.x - cx) / cw) * 100,
    topPct: ((rect.y - cy) / ch) * 100,
    widthPct: (rect.w / cw) * 100,
    heightPct: (rect.h / ch) * 100,
  };
}

/** True when the crop is smaller than the full frame (i.e. zoomed in / pannable). */
export function isFocusZoomed(crop: FocusCrop | null | undefined): boolean {
  if (!crop) return false;
  return crop.wMilli < 1000 || crop.hMilli < 1000;
}

/**
 * Derive the slider zoom percentage from a resolved frame's crop. A full (or
 * missing) crop reads as 1.0x; otherwise it inverts focusCropSizeForZoom off the
 * smaller side, so an existing/loaded frame lands the slider near its framing.
 */
export function focusZoomPctFromFrame(
  frame: Extract<EditOp, { kind: "frame" }> | undefined,
): number {
  return focusZoomPctFromCrop(frame?.crop);
}
